import { IconButton, Typography } from '@mui/material';
import { headCellsDocumentBirth } from 'civil-status/constants/head-cells';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { DocumentSearchBirthCrumbs } from 'civil-status/constants/breadcrumb';
import {
  DocumentBirthFilterForm,
  DocumentBirthVM,
} from 'civil-status/view-models/document-birth-list';
import * as yup from 'yup';
import FilterDocument from 'civil-status/components/document-birth-list';
import { regexOnlyNumber } from 'shell/constants/regex';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { Visibility } from '@mui/icons-material';
import { SearchDocument } from 'shell/models/search-document';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesDocumentBirthSearch } from '..';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { SelectOperatorChoice } from 'shell/constants/select-choice-operator';

const schema = yup
  .object()
  .shape({
    year: yup.string().matches(regexOnlyNumber, 'Campo numerico').required('Campo obbligatorio'),
    matricola: yup.string().matches(regexOnlyNumber, 'Campo numerico'),
    registry: yup.object().nullable(),
    document: yup.string().matches(regexOnlyNumber, 'Campo numerico'),
    surname: yup.string(),
    name: yup.string(),
    operationSurname: yup.string(),
    operationName: yup.string(),
  })
  .test('', '', (values) => {
    const valuesCasted = values as unknown as DocumentBirthFilterForm;

    if (
      valuesCasted.surname &&
      valuesCasted.surname.includes("'") &&
      valuesCasted.operationSurname !== SelectOperatorChoice.Contains
    ) {
      return new yup.ValidationError(
        'Per le stringhe che contengono l\'apostrofo ripetere la ricerca specificando la parte prima o dopo l\'apostrofo e il criterio "Contiene".',
        undefined,
        'surname'
      );
    }

    if (
      valuesCasted.name?.includes("'") &&
      valuesCasted.operationName !== SelectOperatorChoice.Contains
    ) {
      return new yup.ValidationError(
        'Per le stringhe che contengono l\'apostrofo ripetere la ricerca specificando la parte prima o dopo l\'apostrofo e il criterio "Contiene".',
        undefined,
        'name'
      );
    }

    return true;
  });

export default function DocumentBirthSearchPage(): React.ReactElement {
  const { userProfile } = useUserProfile();

  const [birthlist, getBirthlist] = useHttpFetch<
    HttpResponseList<SearchDocument>,
    Array<DocumentBirthVM>
  >(`${__CIVIL_STATUS_API_URL__}/sima/ricerca-atto`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },
    transformer: (response) => {
      return (
        response.data?.payload?.list?.map((item) => {
          const itemResult: DocumentBirthVM = {
            code: item.cittadinoId?.toString() || '',
            faldoneId: item.faldoneId || '',
            surname: item.cittadinoCognome || '',
            name: item.cittadinoNome || '',
            year: item.registroAnno || '',
            documentNumber: item.attoNumero || '',
            numReg: item.registroNumero || '',
            parte: item.registroParte || '',
            user: item.userLock || '',
            locked: !item.userLock
              ? false
              : item.userLock !== userProfile.datiLogin?.key?.usernameEsteso,
            serie: item.registroSerie || '',
          };
          return itemResult;
        }) || []
      );
    },
  });

  const data = birthlist.data as Array<DocumentBirthVM>;

  const formikForm = useFormik<DocumentBirthFilterForm>({
    initialValues: initialValuesDocumentBirthSearch,
    validationSchema: schema,
    onSubmit: (values) => {
      getBirthlist({
        data: {
          attoNumeroA: values.toDocument || undefined,
          attoNumeroDa: values.document || values.fromDocument || undefined,
          cittadinoCognome: values.surname || undefined,
          cittadinoCognomeSearchType: values.surname ? values.operationSurname : undefined,
          cittadinoId: values.matricola || undefined,
          cittadinoNome: values.name || undefined,
          cittadinoNomeSearchType: values.name ? values.operationName : undefined,
          registroAnno: values.year || undefined,
          registroNumeroA: values.toRegistry?.id || undefined,
          registroNumeroDa: values.registry?.id || values.fromRegistry?.id || undefined,
          registroParte: values.portion || undefined,
          registroSerie: values.serie || undefined,
          tipoAtto: '0', // Nascita
        },
      });
    },
  });

  const filterTable = useFilterTable<DocumentBirthFilterForm>();

  useEffect(() => {
    formikForm.setValues(filterTable.values || initialValuesDocumentBirthSearch);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(formikForm.values);
  }, [formikForm.values]);

  // HTTP Request to get list
  const navigate = useNavigate();

  const onChangePage = useCallback(
    (page) => {
      formikForm.setFieldValue('page', page || 0);
    },
    [formikForm.setFieldValue]
  );

  const onChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...formikForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      formikForm.setValues(newDataset);
    },
    [formikForm.setValues, formikForm.values]
  );

  const lockDocument = useHttpClient(`${__CIVIL_STATUS_API_URL__}/atti-nascita-sima/blocca-atto`, {
    options: {
      errorCatching: true,
      method: 'PUT',
      loading: true,
    },
  });

  const onVisibilityPress = useCallback(
    (cell): Promise<DocumentBirthVM> => {
      return new Promise((resolve) => {
        (
          lockDocument({
            data: {
              key: {
                anno: cell.year || '',
                numeroAtto: cell.documentNumber || '',
                numeroRegistro: cell.numReg || '',
                parte: cell.parte || '',
                serie1: cell.serie || '',
              },
            },
          }) as ResponsePromise<void>
        ).then(() => {
          navigate({
            pathname: generatePath(
              '/stato-civile/nascita/ricerca/dettaglio/:anno/:numeroRegistro/:numeroAtto/:parte/:serie1',
              {
                anno: cell.year || '',
                numeroAtto: cell.documentNumber || '',
                numeroRegistro: cell.numReg || '',
                parte: cell.parte || '',
                serie1: cell.serie === ' ' ? 'null' : cell.serie,
              }
            ),
          });
          resolve(cell);
        });
      });
    },
    [navigate, lockDocument]
  );

  /**
   * The list of document act.
   */
  const list = useMemo(() => {
    return (
      data?.map((item) => ({
        ...item,
        action: (
          <PmiTooltipError
            show={item.locked}
            message={item.locked ? `Atto bloccato dall'utente ${item.user}` : ''}
          >
            <span>
              <IconButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onVisibilityPress(item);
                }}
                disabled={item.locked}
              >
                <Visibility />
              </IconButton>
            </span>
          </PmiTooltipError>
        ),
      })) || []
    );
  }, [data, onVisibilityPress]);

  return (
    <>
      <Breadcrumbs items={DocumentSearchBirthCrumbs} base="stato-civile" />
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Ricerca Atti Nascita - Gestione ed Emissione
      </Typography>
      <div className="pmi-my-3">
        <FilterDocument filterDocumentForm={formikForm} />
      </div>
      {data ? (
        list?.length > 0 ? (
          <Table
            ariaLabel="Lista atti nascita"
            page={formikForm.values.page}
            rowsPerPage={formikForm.values.rowsPerPage}
            body={list}
            count={list.length || 0}
            head={headCellsDocumentBirth}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onVisibilityPress={noop}
            onEditPress={noop}
            onTrashPress={noop}
            orders={formikForm.values.orders}
            isNotLayoutFixed
            noSort
            hideButtons
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </>
  );
}

DocumentBirthSearchPage.displayName = 'home page component';
